"use client";

import { Api } from "@/app/api";
import type {
	CatalogSuggestion,
	Chat,
	ChatMessageApiResponse,
	ChatApiResponse,
	CreateChatBody,
	CreateGroupChatBody,
	ChatMessage,
	ClientSupportGroupMembers,
	User,
} from "@/types";
import { CHAT_CONSTANTS } from "@/app/profile/inbox/(chats)/constants";

import { LAURA_LISTING_AI_CONTEXT_MAX_CHARS } from "@/lib/constants/laura-listing-context";

export type ClientSupportPageContext = {
	type: "service" | "category";
	title: string;
	description?: string;
	url?: string;
	details?: string;
	aiContextSummary?: string;
	categoryTitle?: string;
	sectorTitles?: string[];
	listingRole?: "parent" | "child";
	parentListingTitle?: string;
	excerpt?: string;
	packages?: string[];
	addons?: string[];
	links?: string[];
	filters?: string[];
	highlights?: string[];
};

export const chatInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getChat: builder.query<Chat, { id: string }>({
			query: ({ id }) => ({
				url: "/chats/" + id,
				method: "GET",
			}),
			providesTags: (result) => {
				if (result) return [{ type: "chats", id: result._id }];
				return ["chats"];
			},
		}),
		createChat: builder.mutation<Chat, CreateChatBody>({
			query: (body) => ({
				url: "/chats",
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: [{ type: "chats", id: "LIST" }],
		}),
		createGroupChat: builder.mutation<Chat, CreateGroupChatBody>({
			query: (body) => ({
				url: "/chats/group",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [{ type: "chats", id: "LIST" }],
		}),
		getClientSupportGroupChat: builder.query<Chat | null, void>({
			query: () => ({
				url: "/chats/client-support-group",
				method: "GET",
			}),
			providesTags: (result) =>
				result
					? [
							{ type: "chats", id: result._id },
							{ type: "chats", id: "CLIENT_SUPPORT_GROUP" },
						]
					: [{ type: "chats", id: "CLIENT_SUPPORT_GROUP" }],
		}),
		getClientSupportGroupMembers: builder.query<ClientSupportGroupMembers, void>({
			query: () => ({
				url: "/chats/client-support-group/members",
				method: "GET",
			}),
			providesTags: [{ type: "chats", id: "CLIENT_SUPPORT_GROUP_MEMBERS" }],
		}),
		updateClientSupportGroupMembers: builder.mutation<
			ClientSupportGroupMembers,
			{ adminIds: string[] }
		>({
			query: (body) => ({
				url: "/chats/client-support-group/members",
				method: "PATCH",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [
				{ type: "chats", id: "CLIENT_SUPPORT_GROUP_MEMBERS" },
				{ type: "chats", id: "CLIENT_SUPPORT_GROUP" },
				{ type: "chats", id: "LIST" },
			],
		}),
		sendClientSupportGroupMessage: builder.mutation<
			{ chat: Chat; message: ChatMessage },
			{
				content: string;
				fileUrls: string[];
				catalogSuggestions?: CatalogSuggestion[];
				pageContext?: ClientSupportPageContext;
			}
		>({
			query: (body) => ({
				url: "/chats/client-support-group/messages",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			async onQueryStarted({ content, fileUrls }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					const chatId = data.chat._id;
					dispatch(
						chatInjection.util.updateQueryData(
							"getChatsMessagesById",
							{
								chatId,
								page: 1,
								limit: CHAT_CONSTANTS.MESSAGE_LIMIT,
								sortBy: "createdAt:desc",
							},
							(draft) => {
								if (!draft?.docs) return;
								if (draft.docs.some((message) => message._id === data.message._id))
									return;

								draft.docs.push({
									...data.message,
									content,
									fileUrls,
								});
								draft.totalDocs = Math.max(draft.totalDocs ?? 0, draft.docs.length);
							},
						),
					);

					[1000, 3000, 6000, 10000, 15000].forEach((delay) => {
						setTimeout(() => {
							dispatch(
								chatInjection.util.invalidateTags([
									{ type: "chat-messages", id: chatId },
									{ type: "chats", id: chatId },
									{ type: "chats", id: "LIST" },
									{ type: "chats", id: "CLIENT_SUPPORT_GROUP" },
								]),
							);
						}, delay);
					});
				} catch {
					// The component already shows the mutation error to the user.
				}
			},
			invalidatesTags: (result) => [
				{ type: "chats", id: "LIST" },
				{ type: "chats", id: "CLIENT_SUPPORT_GROUP" },
				...(result?.chat?._id
					? [{ type: "chat-messages" as const, id: result.chat._id }]
					: []),
			],
		}),
		sendChatTypingStatus: builder.mutation<
			{ success: boolean },
			{ chatId: string; isTyping: boolean }
		>({
			query: ({ chatId, isTyping }) => ({
				url: `/chats/${chatId}/typing`,
				method: "POST",
				body: { isTyping },
				headers: { "Content-Type": "application/json" },
			}),
		}),
		updateLauraControl: builder.mutation<Chat, { chatId: string; action: "stop" | "start" }>({
			query: ({ chatId, action }) => ({
				url: `/chats/${chatId}/laura-control`,
				method: "PATCH",
				body: { action },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { chatId }) => [
				{ type: "chats", id: chatId },
				{ type: "chats", id: "LIST" },
				{ type: "chats", id: "CLIENT_SUPPORT_GROUP" },
			],
		}),
		updateAiKnowledgeDraftStatus: builder.mutation<
			ChatMessage,
			{ chatId: string; messageId: string; status: "accepted" | "rejected" }
		>({
			query: ({ chatId, messageId, status }) => ({
				url: `/chats/${chatId}/messages/${messageId}/ai-knowledge-draft`,
				method: "PATCH",
				body: { status },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { chatId }) => [{ type: "chat-messages", id: chatId }],
		}),
		removeSupportTicketDraft: builder.mutation<
			ChatMessage,
			{ chatId: string; messageId: string }
		>({
			query: ({ chatId, messageId }) => ({
				url: `/chats/${chatId}/messages/${messageId}/support-ticket-draft/remove`,
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { chatId }) => [{ type: "chat-messages", id: chatId }],
		}),
		markSupportTicketDraftSubmitted: builder.mutation<
			ChatMessage,
			{ chatId: string; messageId: string; ticketId: string }
		>({
			query: ({ chatId, messageId, ticketId }) => ({
				url: `/chats/${chatId}/messages/${messageId}/support-ticket-draft/submitted`,
				method: "PATCH",
				body: { ticketId },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { chatId }) => [{ type: "chat-messages", id: chatId }],
		}),
		deleteChatFromInbox: builder.mutation<{ message: string }, string>({
			query: (chatId) => ({
				url: `/chats/${chatId}/inbox`,
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: (_, __, chatId) => [
				{ type: "chats", id: chatId },
				{ type: "chats", id: "LIST" },
			],
		}),
		getAllChats: builder.query<
			ChatApiResponse,
			{ id: string; page: number; limit?: number; archived?: boolean }
		>({
			query: ({ id, page, limit, archived }) => ({
				url: "/chats",
				params: {
					userId: id,
					page,
					limit,
					archived,
				},
				method: "GET",
				cache: "no-store",
			}),
			providesTags: (result) => {
				if (result) {
					return [
						...result.docs.map(({ _id }) => ({ type: "chats" as const, id: _id })),
						{ type: "chats" as const, id: "LIST" },
					];
				}

				return [{ type: "chats" as const, id: "LIST" }];
			},
		}),
		getChatsMessagesById: builder.query<
			ChatMessageApiResponse,
			{ chatId: string; page: number; limit: number; sortBy?: `createdAt:${"asc" | "desc"}` }
		>({
			query: ({ chatId, page, limit, sortBy }) => ({
				url: `/chats/${chatId}/messages`,
				params: {
					page,
					limit,
					sort: sortBy ?? "createdAt:asc",
				},
				method: "GET",
				cache: "no-store",
			}),
			providesTags: (_, __, p) => {
				return [{ type: "chat-messages", id: p.chatId }];
			},
		}),
		sendChatsMessagesById: builder.mutation<
			ChatMessageApiResponse,
			{
				chatId: string;
				content: string;
				fileUrls: string[];
				catalogSuggestions?: CatalogSuggestion[];
				asCustomerSupport?: boolean;
				senderId?: string;
				expectAsyncReply?: boolean;
				clientSupportSenderRole?: "CUSTOMER" | "STAFF";
				replyToMessageId?: string | null;
				pageContext?: ClientSupportPageContext;
				aiKnowledgeEntry?: {
					title: string;
					content: string;
					tags?: string[];
					entryType: "KNOWLEDGE" | "RULE";
					isActive?: boolean;
				};
			}
		>({
			query: ({
				chatId,
				content,
				fileUrls,
				catalogSuggestions,
				asCustomerSupport,
				aiKnowledgeEntry,
				clientSupportSenderRole,
				replyToMessageId,
				pageContext,
			}) => ({
				url: `/chats/${chatId}/messages`,
				method: "POST",
				body: {
					content: content,
					chatId: chatId,
					fileUrls: fileUrls,
					catalogSuggestions,
					cs: asCustomerSupport,
					clientSupportSenderRole,
					replyToMessageId,
					pageContext,
					aiKnowledgeEntry,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			async onQueryStarted(
				{
					chatId,
					content,
					fileUrls,
					catalogSuggestions,
					senderId,
					expectAsyncReply,
					clientSupportSenderRole,
				},
				{ dispatch, queryFulfilled },
			) {
				// Optimistically update the cache - update the first page query (most common case)
				const patchResult = dispatch(
					chatInjection.util.updateQueryData(
						"getChatsMessagesById",
						{
							chatId,
							page: 1,
							limit: CHAT_CONSTANTS.MESSAGE_LIMIT,
							sortBy: "createdAt:desc",
						},
						(draft) => {
							if (!draft) return;

							// Create optimistic message
							const optimisticMessage: ChatMessage = {
								_id: `temp-${Date.now()}-${Math.random()}`,
								content,
								senderId: senderId ? ({ _id: senderId } as any) : null,
								chatId,
								replyToMessageId: null,
								isRead: false,
								fileUrls,
								catalogSuggestions,
								clientSupportSenderRole,
								createdAt: new Date().toISOString(),
								updatedAt: new Date().toISOString(),
								__v: 0,
								id: `temp-${Date.now()}-${Math.random()}`,
							};

							// Messages are sorted ascending (oldest first), so add to end
							draft.docs.push(optimisticMessage);
							draft.totalDocs = draft.docs.length;
						},
					),
				);

				try {
					await queryFulfilled;
					if (expectAsyncReply) {
						[2500, 5000, 9000].forEach((delay) => {
							setTimeout(() => {
								dispatch(
									chatInjection.util.invalidateTags([
										{ type: "chat-messages", id: chatId },
										{ type: "chats", id: chatId },
										{ type: "chats", id: "LIST" },
									]),
								);
							}, delay);
						});
					}
				} catch {
					// If the mutation fails, rollback the optimistic update
					patchResult.undo();
				}
			},
			invalidatesTags: (_, __, p) => {
				return [
					{ type: "chat-messages", id: p.chatId },
					{ type: "chats", id: p.chatId },
					{ type: "chats", id: "LIST" }, // Also invalidate chat list to update lastMessage
				];
			},
		}),
		readChatsMessagesById: builder.mutation<
			ChatMessageApiResponse,
			{ chatId: string; messageId: string; asCustomerSupport?: boolean }
		>({
			query: ({ chatId, messageId, asCustomerSupport }) => ({
				url: `/chats/${chatId}/messages/${messageId}`,
				method: "PATCH",
				body: {
					isRead: true,
					cs: asCustomerSupport,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			async onQueryStarted({ chatId, messageId }, { dispatch, queryFulfilled }) {
				// Optimistically update the message in the cache
				const patchResults: Array<() => void> = [];

				// Update all message queries for this chat
				// We need to update all pages that might contain this message
				for (let page = 1; page <= 10; page++) {
					// Check up to 10 pages
					try {
						const patchResult = dispatch(
							chatInjection.util.updateQueryData(
								"getChatsMessagesById",
								{ chatId, page, limit: CHAT_CONSTANTS.MESSAGE_LIMIT },
								(draft) => {
									if (!draft?.docs) return;

									// Find and update the message
									const message = draft.docs.find(
										(msg) => msg._id === messageId || msg.id === messageId,
									);
									if (message) {
										message.isRead = true;
									}
								},
							),
						);
						patchResults.push(patchResult.undo);
					} catch (error) {
						// Query might not exist in cache, that's okay
					}
				}

				try {
					await queryFulfilled;
				} catch (error) {
					// Revert optimistic updates on error
					patchResults.forEach((undo) => undo());
				}
			},
			invalidatesTags: (_, __, p) => [
				{ type: "chat-messages", id: p.chatId },
				{ type: "chats", id: p.chatId },
				{ type: "chats", id: "LIST" },
			],
		}),
		getCustomerSupportAccount: builder.query<User | null, void>({
			queryFn: async (_args, _api, _extraOptions, baseQuery) => {
				const result = await baseQuery({
					url: "/chats/customer-support-account",
					method: "GET",
				});

				// CRITICAL: Check response status from meta first (most reliable)
				const responseStatus = (result as any).meta?.response?.status;
				if (responseStatus === 400) {
					return { data: null };
				}

				// Check if there's an error
				if (result.error) {
					const errorStatus =
						(result.error as any).status || (result.error as any).data?.statusCode;

					// Handle 400 errors gracefully (when support account is not configured)
					// Return null instead of throwing an error
					if (errorStatus === 400) {
						return { data: null };
					}

					// For all other error statuses (401, 500, etc.), return the error as-is
					// CRITICAL: Ensure we don't return {data: undefined} - RTK Query requires either data or error
					// If result has both error and undefined data, return only error
					if (result.data === undefined) {
						// Remove undefined data from error result
						return { error: result.error, meta: (result as any).meta };
					}

					return result;
				}

				// CRITICAL: Check if data is undefined or null (when no error)
				// This prevents returning {data: undefined} which RTK Query rejects
				if (result.data === undefined || result.data === null) {
					// If data is undefined/null, return null (safer than undefined)
					return { data: null };
				}

				// If no error and data exists, return the result with data
				return result as { data: User };
			},
			providesTags: [{ type: "user", id: "support-account" }],
		}),
		markChatAsRead: builder.mutation<
			{ success: boolean; messagesUpdated: number },
			{
				chatId: string;
				userId?: string;
				asCustomerSupport?: boolean;
				supportView?: "customer" | "staff";
			}
		>({
			query: ({ chatId, asCustomerSupport, supportView }) => {
				const body: { isRead: boolean; cs?: boolean; supportView?: "customer" | "staff" } =
					{
						isRead: true,
					};
				if (asCustomerSupport !== undefined) {
					body.cs = asCustomerSupport;
				}
				if (supportView !== undefined) {
					body.supportView = supportView;
				}
				return {
					url: `/chats/${chatId}/messages/read`,
					method: "PATCH",
					body: body,
					headers: {
						"Content-Type": "application/json",
					},
				};
			},
			async onQueryStarted(
				{ chatId, userId, supportView },
				{ dispatch, queryFulfilled, getState },
			) {
				// Optimistically clear unread counts in chat list and chat detail
				const patchQueries: Array<() => void> = [];
				let apiSucceeded = false;
				const clearUnreadCounts = (target: {
					unreadCount?: number;
					clientSupportCustomerUnreadCount?: number;
					clientSupportStaffUnreadCount?: number;
				}) => {
					if (supportView === "customer") {
						target.clientSupportCustomerUnreadCount = 0;
						return;
					}
					if (supportView === "staff") {
						target.clientSupportStaffUnreadCount = 0;
						return;
					}
					target.unreadCount = 0;
				};
				const shouldMarkMessageRead = (message: {
					senderId: string | { _id?: string } | null;
					clientSupportSenderRole?: "CUSTOMER" | "STAFF" | "";
				}) => {
					const messageSenderId =
						typeof message.senderId === "string"
							? message.senderId
							: message.senderId?._id;
					if (supportView === "staff") {
						return (
							messageSenderId !== userId ||
							message.clientSupportSenderRole === "CUSTOMER"
						);
					}
					if (supportView === "customer") {
						return (
							messageSenderId !== userId ||
							message.clientSupportSenderRole === "STAFF"
						);
					}
					return Boolean(messageSenderId && messageSenderId !== userId);
				};

				// Helper function to apply optimistic updates
				const applyOptimisticUpdate = () => {
					if (!userId) return;

					// Update the exact query args used in ChatsBar and page.tsx
					const exactArgVariants = [
						{ id: userId, page: 1, limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT },
						{
							id: userId,
							page: 1,
							limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT,
							archived: false,
						},
					];
					exactArgVariants.forEach((exactArgs) => {
						try {
							dispatch(
								chatInjection.util.updateQueryData(
									"getAllChats",
									exactArgs,
									(draft) => {
										if (!draft?.docs) return;
										const target = draft.docs.find(
											(chat) => chat._id === chatId,
										);
										if (target) {
											// Always update, even if unreadCount is already 0, to ensure consistency
											clearUnreadCounts(target);
											if (target.lastMessage) {
												target.lastMessage.isRead = true;
											}
										}
									},
								),
							);
						} catch (error) {
							// Failed to apply optimistic update
						}
					});
				};

				// Apply optimistic update immediately
				applyOptimisticUpdate();

				// Find all getAllChats queries in the cache using RTK Query's utilities
				const invalidatedQueries = chatInjection.util.selectInvalidatedBy(getState(), [
					{ type: "chats", id: "LIST" },
				]);

				// Update each getAllChats query that would be affected
				invalidatedQueries.forEach((query) => {
					if (query.endpointName === "getAllChats" && query.originalArgs) {
						const args = query.originalArgs as {
							id: string;
							page: number;
							limit?: number;
						};

						// Only update queries for the same userId
						if (!userId || args.id === userId) {
							try {
								const patch = dispatch(
									chatInjection.util.updateQueryData(
										"getAllChats",
										args,
										(draft) => {
											if (!draft?.docs) {
												return;
											}
											const target = draft.docs.find(
												(chat) => chat._id === chatId,
											);
											if (target) {
												clearUnreadCounts(target);
												if (target.lastMessage) {
													target.lastMessage.isRead = true;
												}
											}
										},
									),
								);
								patchQueries.push(patch.undo);
							} catch (error) {
								// Failed to update cached query
							}
						}
					}
				});

				// Also try the exact query args used in ChatsBar as a fallback
				if (userId) {
					const exactArgVariants = [
						{ id: userId, page: 1, limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT },
						{
							id: userId,
							page: 1,
							limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT,
							archived: false,
						},
					];
					exactArgVariants.forEach((exactArgs) => {
						try {
							const patch = dispatch(
								chatInjection.util.updateQueryData(
									"getAllChats",
									exactArgs,
									(draft) => {
										if (!draft?.docs) return;
										const target = draft.docs.find(
											(chat) => chat._id === chatId,
										);
										if (target) {
											clearUnreadCounts(target);
											if (target.lastMessage) {
												target.lastMessage.isRead = true;
											}
										}
									},
								),
							);
							patchQueries.push(patch.undo);
						} catch (error) {
							// Failed to update with exact args
						}
					});
				}

				// Update the specific chat query
				try {
					const patchChat = dispatch(
						chatInjection.util.updateQueryData("getChat", { id: chatId }, (draft) => {
							if (draft) {
								clearUnreadCounts(draft);
								if (draft.lastMessage) {
									draft.lastMessage.isRead = true;
								}
							}
						}),
					);
					patchQueries.push(patchChat.undo);
				} catch {
					// Cache entry might not exist yet; ignore
				}

				const patchMessageCache = (args: {
					chatId: string;
					page: number;
					limit: number;
					sortBy?: `createdAt:${"asc" | "desc"}`;
				}) => {
					const patchMessages = dispatch(
						chatInjection.util.updateQueryData(
							"getChatsMessagesById",
							args,
							(draft) => {
								if (!draft?.docs) return;
								draft.docs.forEach((message) => {
									if (shouldMarkMessageRead(message)) {
										message.isRead = true;
									}
								});
							},
						),
					);
					patchQueries.push(patchMessages.undo);
				};

				// Optimistically update the visible chat-page cache and the default cache shape.
				// The chat page uses sortBy, so missing that key leaves stale unread messages visible.
				try {
					patchMessageCache({
						chatId,
						page: 1,
						limit: CHAT_CONSTANTS.MESSAGE_LIMIT,
						sortBy: "createdAt:desc",
					});
					patchMessageCache({ chatId, page: 1, limit: CHAT_CONSTANTS.MESSAGE_LIMIT });
				} catch (error) {
					// Failed to update messages cache
				}

				try {
					await queryFulfilled;
					apiSucceeded = true;

					// Re-apply optimistic update after API succeeds
					// The backend should have the correct data now, but this ensures consistency
					// even if polling refetches before the backend fully propagates the change
					applyOptimisticUpdate();

					// Re-apply message updates
					try {
						patchMessageCache({
							chatId,
							page: 1,
							limit: CHAT_CONSTANTS.MESSAGE_LIMIT,
							sortBy: "createdAt:desc",
						});
						patchMessageCache({ chatId, page: 1, limit: CHAT_CONSTANTS.MESSAGE_LIMIT });
					} catch (error) {
						// Failed to re-apply message updates after success
					}
				} catch (error: any) {
					// rollback on error
					patchQueries.forEach((undo) => undo());
					console.error("[chatApi] markChatAsRead failed", {
						chatId,
						error,
						status: error?.status,
						statusText: error?.statusText,
						data: error?.data,
						errorData: error?.error?.data,
						message: error?.error?.message || error?.message,
						validationErrors: error?.data?.message || error?.error?.data?.message,
						fullError: JSON.stringify(error, null, 2),
					});
				}
			},
			invalidatesTags: () => {
				// Don't invalidate - rely on optimistic updates and polling
				// Invalidating causes immediate refetch which can overwrite optimistic updates
				// Polling will eventually get the correct data from backend
				return [];
			},
		}),
		markChatAsUnread: builder.mutation<
			{ success: boolean; messagesUpdated: number },
			{ chatId: string; asCustomerSupport?: boolean }
		>({
			query: ({ chatId, asCustomerSupport }) => ({
				url: `/chats/${chatId}/messages/unread`,
				method: "PATCH",
				body: { isRead: false, cs: asCustomerSupport },
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: (_, __, { chatId }) => [
				{ type: "chat-messages", id: chatId },
				{ type: "chats", id: chatId },
				{ type: "chats", id: "LIST" },
			],
		}),
	}),
});

export const {
	useCreateChatMutation,
	useCreateGroupChatMutation,
	useDeleteChatFromInboxMutation,
	useGetClientSupportGroupChatQuery,
	useLazyGetClientSupportGroupChatQuery,
	useGetClientSupportGroupMembersQuery,
	useUpdateClientSupportGroupMembersMutation,
	useSendClientSupportGroupMessageMutation,
	useSendChatTypingStatusMutation,
	useUpdateLauraControlMutation,
	useUpdateAiKnowledgeDraftStatusMutation,
	useRemoveSupportTicketDraftMutation,
	useMarkSupportTicketDraftSubmittedMutation,
	useGetChatQuery,
	useGetAllChatsQuery,
	useGetChatsMessagesByIdQuery,
	useLazyGetChatsMessagesByIdQuery,
	useSendChatsMessagesByIdMutation,
	useReadChatsMessagesByIdMutation,
	useMarkChatAsReadMutation,
	useMarkChatAsUnreadMutation,
	useGetCustomerSupportAccountQuery,
} = chatInjection;
